import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { WipeLink } from './Wipe';
import { CHAPTERS, EASE } from '../data/site';

const Navbar = () => {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    let raf = null;
    const check = () => {
      raf = null;
      const nav = headerRef.current;
      const els = document.elementsFromPoint(Math.round(window.innerWidth / 2), 24);
      let node = els.find((e) => nav && !nav.contains(e));
      while (node && node !== document.body) {
        const bg = getComputedStyle(node).backgroundColor;
        const m = bg && bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (m && (m[4] === undefined || parseFloat(m[4]) > 0.5)) {
          const lum = 0.299 * Number(m[1]) + 0.587 * Number(m[2]) + 0.114 * Number(m[3]);
          setDark(lum < 145);
          return;
        }
        node = node.parentElement;
      }
      setDark(false);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    const t = setTimeout(check, 150);
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname]);

  return (
    <>
    <motion.header
      ref={headerRef}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md transition-colors duration-300 ${dark ? 'text-paper' : 'text-ink'}`}
      data-testid="main-navbar"
    >
      <div className="flex items-center justify-between px-5 md:px-10 py-3.5 md:py-4">
        <WipeLink to="/" data-testid="nav-home-link" className="flex items-center group">
          <span
            role="img"
            aria-label="Designing for the Next Phase of India's Offline Retail"
            className="block h-7 md:h-8 bg-current transition-colors duration-300"
            style={{
              aspectRatio: '2000 / 681',
              WebkitMaskImage: "url('/assets/logo/alt1.png')",
              maskImage: "url('/assets/logo/alt1.png')",
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
            }}
          />
        </WipeLink>
        <nav className="hidden md:flex items-center gap-5" data-testid="nav-chapters">
          {CHAPTERS.map((c) => (
            <WipeLink
              key={c.slug}
              to={`/chapter/${c.slug}`}
              data-testid={`nav-chapter-${c.slug}`}
              className={`text-xs md:text-sm font-semibold tracking-widest transition-opacity duration-300 hover:opacity-50 ${
                pathname.includes(c.slug) ? 'opacity-100 border-b border-current pb-1' : 'opacity-70'
              }`}
            >
              {c.n}
            </WipeLink>
          ))}
          <WipeLink
            to="/saarthi"
            data-testid="nav-saarthi-link"
            className={`text-xs md:text-sm font-semibold tracking-widest transition-opacity duration-300 hover:opacity-50 ${
              pathname.includes('saarthi') ? 'opacity-100 border-b border-current pb-1' : 'opacity-70'
            }`}
          >
            CASE STUDY
          </WipeLink>
          <WipeLink
            to="/about"
            data-testid="nav-about-link"
            className={`text-xs md:text-sm font-semibold tracking-widest transition-opacity duration-300 hover:opacity-50 ${
              pathname.includes('about') ? 'opacity-100 border-b border-current pb-1' : 'opacity-70'
            }`}
          >
            AUTHOR
          </WipeLink>
        </nav>
        <button
          onClick={() => setMenuOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
          className="md:hidden p-1"
        >
          <Menu size={26} />
        </button>
      </div>
    </motion.header>

    <AnimatePresence>
      {menuOpen && (
        <motion.div
          data-testid="mobile-menu"
          initial={{ clipPath: 'inset(0 0 100% 0)' }}
          animate={{ clipPath: 'inset(0 0 0% 0)' }}
          exit={{ clipPath: 'inset(0 0 100% 0)' }}
          transition={{ duration: 0.7, ease: EASE }}
          className="fixed inset-0 z-[70] bg-brand-black text-paper flex flex-col"
        >
          <div className="bandhani-dot absolute inset-0 text-paper/[0.05] pointer-events-none" />
          <div className="flex items-center justify-between px-5 py-5">
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-paper/60">The Book</span>
            <button
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Close menu"
              className="p-1 text-paper/80 transition-colors duration-300 hover:text-paper"
            >
              <X size={28} />
            </button>
          </div>
          <nav className="relative flex-1 flex flex-col justify-center px-6">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, x: -32 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + i * 0.08, duration: 0.6, ease: EASE }}
              >
                <WipeLink
                  to={`/chapter/${c.slug}`}
                  data-testid={`mobile-menu-chapter-${c.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="group flex items-baseline gap-5 border-b border-white/10 py-5"
                >
                  <span
                    className="text-sm font-medium tracking-widest"
                    style={{ color: { '#20431B': '#6FA85C', '#01114F': '#5B78E8' }[c.color] || c.color }}
                  >
                    {c.n}
                  </span>
                  <span className="text-4xl font-light tracking-tight transition-transform duration-500 group-active:translate-x-2">
                    {c.title}
                  </span>
                </WipeLink>
              </motion.div>
            ))}
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.62, duration: 0.6, ease: EASE }}
            className="relative px-6 pb-6 space-y-3"
          >
            <WipeLink
              to="/saarthi"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-menu-saarthi"
              className="block text-sm font-medium uppercase tracking-[0.25em] text-brand-pink"
            >
              The Case Study, Retail Saarthi
            </WipeLink>
            <WipeLink
              to="/about"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-menu-author"
              className="block text-sm font-medium uppercase tracking-[0.25em] text-paper/70"
            >
              The Author
            </WipeLink>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6, ease: EASE }}
            className="relative px-6 pb-10 text-xs uppercase tracking-[0.25em] text-paper/50 space-y-2"
          >
            <p>A Graduation Project, Aditya Ranka · 2026</p>
            <p>
              <a href="mailto:contact@adityarankaa.com" data-testid="mobile-menu-mail" className="text-paper/70">contact@adityarankaa.com</a>
              <span className="mx-3 text-paper/30">·</span>
              <a href="tel:+919826025454" data-testid="mobile-menu-phone" className="text-paper/70">[IN] +91 98260 25454</a>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default Navbar;
